/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import HelpIcon from '@mui/icons-material/Help';
import {
  alpha,
  Box,
  ClickAwayListener,
  Fade,
  Grid,
  IconButton,
  Paper,
  Popper,
  Stack,
  type Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import DocsIcon from './icons/book-open.svg?react';
import GetSupportIcon from './icons/question-help-message.svg?react';

const DOCS_BASE_PATH = 'https://docs.poweralpha.com';
const SUPPORT_MAILBOX = 'support.poweralpha@brightnightpower.com';

const getDocsHoverSx = (theme: Theme) => ({
  pt: 1,
  pl: 1.5,
  pr: 1.5,
  '&:hover': {
    backgroundColor: alpha(theme.palette.info.main, 0.1),
  },
});

const getSupportOptionSx = (theme: Theme) => ({
  p: 1,
  pl: 1.5,
  pr: 1.5,
  width: '100%',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
});

const paperSx = {
  width: 300,
  marginTop: 3,
  zIndex: 5,
  cursor: 'pointer',
};

function PASupport() {
  const theme = useTheme();
  const anchorEl = useRef<HTMLButtonElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);

  const handleClick = useCallback(() => setIsOpen((prev) => !prev), []);

  const handleDocsClick = useCallback(() => {
    window.open(DOCS_BASE_PATH);
    handleClick();
  }, [handleClick]);

  const handleSupportClick = useCallback(() => {
    const body = encodeURIComponent(
      '\r\n\r\n------------------------------------------------------------\r\n' +
        'Do not make changes below this line\r\n## [PA TICKET] ##',
    );
    window.open(`mailto:${SUPPORT_MAILBOX}?body=${body}`);
    handleClick();
  }, [handleClick]);

  const handleClickAway = useCallback(() => {
    if (!isOpen) return;
    setIsOpen(false);
  }, [isOpen]);

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box>
        <Tooltip title="Support">
          <IconButton onClick={handleClick} ref={anchorEl} size="small" color="secondary">
            <HelpIcon />
          </IconButton>
        </Tooltip>
        <Popper
          open={isOpen}
          placement="bottom-end"
          anchorEl={anchorEl.current}
          transition
          sx={{ zIndex: 1e3 }}
          onClick={(e) => e.stopPropagation()}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={300}>
              <Paper elevation={3} sx={paperSx}>
                <Stack>
                  <Box
                    onClick={handleDocsClick}
                    sx={(muiTheme) => ({ flex: 1, ...getDocsHoverSx(muiTheme) })}
                  >
                    <Stack direction="row">
                      <Grid size="grow">
                        <Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 'fontWeightBold',
                            color: 'info.main',
                          }}
                        >
                          Visit the docs
                        </Typography>
                      </Grid>
                      <Grid>
                        <DocsIcon height={22} color={theme.palette.info.main} />
                      </Grid>
                    </Stack>
                  </Box>
                  <Box onClick={handleSupportClick} sx={getSupportOptionSx}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <Stack>
                        <Typography
                          sx={{
                            fontSize: 11,
                          }}
                        >
                          Still need help? Would like to propose a feature?
                        </Typography>
                        <Grid size="grow">
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: 'fontWeightBold',
                            }}
                          >
                            Submit a ticket!
                          </Typography>
                        </Grid>
                      </Stack>
                      <Grid>
                        <GetSupportIcon height={22} color={theme.palette.warning.main} />
                      </Grid>
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            </Fade>
          )}
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

export default PASupport;
